import { SortDirection, SortType, useHomeStore } from '../../store/home-store';
import {
  InteractBankIcon,
  InteractCalendarIcon,
  InteractCheckIcon,
  InteractCoinIcon,
  InteractLiveIcon,
  InteractNewIcon,
  InteractSortIcon,
  InteractSparkleIcon,
  InteractTrendingIcon,
  InteractTrophyIcon,
  InteractWatchedIcon,
  InteractWatchIcon,
} from '@/client/assets';
import { ApiTranslation } from '@/client/components/api-translation';
import { AuthedButton } from '@/client/components/authed-button';
import { ScrollArea, ScrollBar } from '@/client/components/ui/scroll-area';
import { Skeleton } from '@/client/components/ui/skeleton';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { formatFirstUpperCase } from '@/client/utils/format';
import { useCategories } from '@/libs/react-sdk/api/use-categories';
import { memo, ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { readActivePill, type PillRect } from './filter-pill';

export type FilterItem = {
  value: string;
  label: ReactNode | string;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  color?: string;
  // Tailwind text-color classes applied to label + icon when inactive (e.g. the
  // Polymarket-style gold used to make the World Cup category stand out).
  accent?: string;
  translate?: boolean;
  // Label is already display-ready (localized client-side); render as-is,
  // skipping both i18next lookup and ApiTranslation.
  plain?: boolean;
} & {
  translate?: true;
  label: string;
};

// Category icon mapping (Polymarket-style pill icons). Name-keyed so it works
// across envs where category ids differ; unknown categories render text-only.
const getCategoryIcon = (name: string): React.FC<React.SVGProps<SVGSVGElement>> | undefined => {
  const n = name.toLowerCase();
  if (/crypto|加密/.test(n)) return InteractCoinIcon;
  // \b keeps "ai" from matching inside other words (e.g. "Daily").
  if (/\bai\b|tech|科技/.test(n)) return InteractSparkleIcon;
  if (/macro|econom|宏觀|宏观/.test(n)) return InteractBankIcon;
  if (/daily|weekly|每日|每週/.test(n)) return InteractCalendarIcon;
  if (/sport|體育|体育/.test(n)) return InteractTrophyIcon;
  return undefined;
};

// Category display labels, name-keyed like getCategoryIcon. Admin categories
// arrive from the API without i18n maps, so known names are localized
// client-side; unknown names (and the en locale) fall back to the raw admin
// name and go through ApiTranslation as before.
const CATEGORY_LABELS: { match: RegExp; labels: Record<string, string> }[] = [
  { match: /世界盃|世界杯|world\s?cup/i, labels: { 'zh-hk': '世界盃', es: 'Mundial' } },
  { match: /crypto|加密/i, labels: { 'zh-hk': '加密貨幣', es: 'Cripto' } },
  { match: /macro|econom|宏觀|宏观/i, labels: { 'zh-hk': '宏觀', es: 'Macro' } },
  { match: /daily|weekly|每日|每週/i, labels: { 'zh-hk': '每日', es: 'Diario' } },
  { match: /sport|體育|体育/i, labels: { 'zh-hk': '體育', es: 'Deportes' } },
  { match: /\bai\b|tech|科技/i, labels: { 'zh-hk': 'AI 與科技', es: 'IA y tecnología' } },
];

const getCategoryLabel = (name: string, lang: string): string | undefined =>
  CATEGORY_LABELS.find(entry => entry.match.test(name))?.labels[lang.toLowerCase()];

// Category color mapping
const getCategoryColor = (name: string): string => {
  const colorMap: Record<string, string> = {
    crypto: 'bg-orange-500',
    politics: 'bg-blue-500',
    sports: 'bg-green-500',
    tech: 'bg-cyan-500',
    entertainment: 'bg-pink-500',
    economy: 'bg-indigo-500',
    science: 'bg-purple-500',
    culture: 'bg-rose-500',
    world: 'bg-slate-500',
    finance: 'bg-emerald-500',
  };
  return colorMap[name.toLowerCase()] || 'bg-gray-500';
};

const defaultSortType = [
  {
    value: 'trending',
    label: 'all',
    icon: InteractTrendingIcon,
    color: 'bg-purple-500',
  },
  {
    value: 'live',
    label: 'live',
    icon: InteractLiveIcon,
    color: 'bg-red-500',
  },
  {
    value: 'new',
    label: 'new',
    icon: InteractNewIcon,
    color: 'bg-blue-500',
  },
  {
    value: 'ended',
    label: 'ended',
    icon: InteractCheckIcon,
    color: 'bg-amber-500',
  },
] as const;

type CategoryItemProps = {
  item: FilterItem;
  translate?: boolean;
  isActive: boolean;
  onClick: () => void;
};

export const CategoryItem = ({ item, translate = true, isActive, onClick }: CategoryItemProps) => {
  const { t } = useTranslation(['home']);
  
  return (
    <button
      type="button"
      aria-label={item.label}
      data-active={isActive}
      onClick={onClick}
      className="neon-pill relative px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap shrink-0 outline-none"
    >
      {/* Content */}
      <span
        className={cn(
          'relative z-10 flex items-center gap-1.5 transition-colors duration-200',
          isActive ? 'text-background' : item.accent ?? 'text-muted-foreground hover:text-foreground'
        )}
      >
        {item.icon ? (
          <item.icon className="size-3.5 fill-current" />
        ) : (
          <span className={cn('size-2 rounded-full', item.color || 'bg-gray-500')} />
        )}
        {item.plain ? item.label : item.translate || translate ? t(item.label) : <ApiTranslation>{item.label}</ApiTranslation>}
      </span>
    </button>
  );
};

export const CategoryItemSkeleton = memo(() => {
  return <Skeleton className="h-8 w-20 rounded-full mx-1" />;
});

CategoryItemSkeleton.displayName = 'CategoryItemSkeleton';

export const CategorySkeleton = ({ len }: { len: number }) => {
  return Array.from({ length: len }).map((_, index) => <CategoryItemSkeleton key={`CategoryItemSkeleton-${index}`} />);
};

type SortItemProps = {
  children?: React.ReactNode;
  sortDirection?: SortDirection;
  setSortDirection?: (sortDirection: SortDirection) => void;
};

export const SortItem = ({ children, sortDirection, setSortDirection }: SortItemProps) => {
  const [direction, setDirection] = useState<SortDirection>('default');
  useEffect(() => {
    if (sortDirection) setDirection(sortDirection);
  }, [sortDirection]);
  const handleToggle = () => {
    const next = direction == 'default' ? 'desc' : direction == 'desc' ? 'asc' : 'default';
    setDirection(next);
    setSortDirection?.(next);
  };
  return (
    <div
      className="flex items-center gap-1 px-2 py-1.5 text-sm cursor-pointer whitespace-nowrap text-muted-foreground hover:text-foreground transition-colors"
      onClick={handleToggle}
    >
      {children}
      <InteractSortIcon
        className={cn(
          'size-4 fill-muted-foreground/30 transition-colors',
          direction === 'asc' && '[&_path:first-child]:fill-foreground',
          direction === 'desc' && '[&_path:last-child]:fill-foreground'
        )}
      />
    </div>
  );
};

export const TheMarketListFilterBar = () => {
  const { t, i18n } = useTranslation(['home']);
  const category = useHomeStore(state => state.category);
  const setCategory = useHomeStore(state => state.setCategory);
  const sortDirection = useHomeStore(state => state.sortDirection);
  const setSortDirection = useHomeStore(state => state.setSortDirection);
  const sortType = useHomeStore(state => state.sortType);
  const setSortType = useHomeStore(state => state.setSortType);
  const { data, isLoading } = useCategories(api, {});
  const categories = useMemo(
    () =>
      (data?.categories ?? [])
        .slice()
        .sort((a, b) => b.weight - a.weight)
        .map(cat => {
          const localizedLabel = getCategoryLabel(cat.name, i18n.language);
          return {
            label: localizedLabel ?? formatFirstUpperCase(cat.name),
            plain: !!localizedLabel,
            value: cat.id,
            color: getCategoryColor(cat.name),
            icon: getCategoryIcon(cat.name),
          };
        }),
    [data?.categories, i18n.language]
  );

  const groupRef = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState<PillRect | null>(null);
  const measurePill = useCallback(() => setPill(readActivePill(groupRef.current)), []);

  useLayoutEffect(measurePill, [measurePill, category, categories, isLoading]);
  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;

    const observer = new ResizeObserver(measurePill);
    observer.observe(group);
    for (const child of Array.from(group.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [measurePill, categories.length, isLoading]);

  return (
    <div className="flex items-center gap-4 px-4 md:px-6 h-12 bg-background border-b border-border pos-sticky top-0 md:top-15 z-10">
      {/* Left: Category tabs */}
      <ScrollArea className="flex-1 overflow-auto">
        <div ref={groupRef} className="relative flex items-center gap-1">
          {pill && (
            <div
              aria-hidden
              className="tab-pill-slide absolute left-0 top-0 bg-foreground rounded-full"
              style={{ width: pill.width, height: pill.height, transform: `translate(${pill.left}px, ${pill.top}px)` }}
            />
          )}
          {defaultSortType.map((item, index) => (
            <CategoryItem
              key={`tag-${item.value}-${index}`}
              item={item}
              isActive={item.value === category}
              onClick={() => setCategory(item.value)}
            />
          ))}

          {categories.length > 0 && <div className="w-px h-5 bg-border/50 mx-1.5 shrink-0" />}

          {categories.map((item, index) => (
            <CategoryItem
              key={`tag-${item.value}-${index}`}
              translate={false}
              item={item}
              isActive={item.value === category}
              onClick={() => setCategory(item.value)}
            />
          ))}
          {isLoading && <CategorySkeleton len={6} />}
        </div>
        <ScrollBar orientation="horizontal" className="h-1.5 cursor-pointer" />
      </ScrollArea>

      {/* Right: Sort + Watchlist */}
      <div className="flex items-center gap-1 shrink-0">
        <SortItem
          sortDirection={sortType == 'expire-time' ? sortDirection : 'default'}
          setSortDirection={direction => {
            setSortType('expire-time');
            setSortDirection(direction);
          }}
        >
          {t('expire-time')}
        </SortItem>
        <SortItem
          sortDirection={sortType == 'volume' ? sortDirection : 'default'}
          setSortDirection={direction => {
            setSortType('volume');
            setSortDirection(direction);
          }}
        >
          {t('volume')}
        </SortItem>

        <div className="w-px h-5 bg-border/50 mx-1" />

        {/* Watchlist button */}
        <button
          type="button"
          onClick={() => setCategory(category == 'watchlist' ? 'trending' : 'watchlist')}
          className="neon-pill relative px-3 py-1.5 h-8 rounded-full text-sm font-medium outline-none"
        >
          {/* Sliding background for watchlist */}
          {category === 'watchlist' && (
            <div className="absolute inset-0 bg-foreground rounded-full tab-pill-in" />
          )}
          
          <span
            className={cn(
              'relative z-10 flex items-center gap-1.5 transition-colors duration-200',
              category === 'watchlist' ? 'text-background' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {category === 'watchlist' ? (
              <InteractWatchedIcon className="size-3.5 fill-current" />
            ) : (
              <InteractWatchIcon className="size-3.5 fill-current" />
            )}
            <span className="hidden sm:inline">{t('watchlist')}</span>
          </span>
        </button>
      </div>
    </div>
  );
};
